import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenantContext } from 'contexts/TenantContext/TenantContext';
import { makeStyles, createStyles } from '@bb-ui/react-library/dist/components/styles';
import { useRestApi } from 'hooks/useRestApi';
import { Switch } from '@bb-ui/react-library/dist/components/Switch';
import { FormControlLabel } from '@bb-ui/react-library/dist/components/FormControlLabel';
import { Tooltip } from '@bb-ui/react-library/dist/components/Tooltip';
import { Typography } from '@bb-ui/react-library/dist/components/Typography';
import { apiUrl } from 'utils/apiUrl';
import { Lock, Unlock } from '@bb-ui/icons/dist/small';
import { useLearnInstance } from 'hooks/useLearnInstance';

const useStyles = makeStyles((theme) =>
  createStyles({
    titleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    container: {
      display: 'flex',
      alignItems: 'center',
      marginLeft: '0.75rem',
    },
    switchLabel: {
      marginBottom: 0,
    },
    lockedBy: {
      marginTop: '2px',
      overflowWrap: 'anywhere',
      fontSize: theme.typography.fontSizeSmall,
    },
    switchBase: {
      padding: 0,
      '&:hover': {
        backgroundColor: 'transparent',
      },
      '&$checked:hover': {
        backgroundColor: 'transparent',
      },
    },
    checked: {},
    track: {
      borderRadius: '1rem',
      width: '2.5rem',
      height: '1.5rem',
      opacity: 1,
      backgroundColor: 'red',
      '&$checked + &': {
        backgroundColor: 'red',
      },
    },
    iconWrapper: {
      width: '1.5rem',
      height: '1.5rem',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.palette.action.disabledBackground,
      '$checked &': {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.common.white,
        marginRight: '-0.0625rem',
      },
    },
  }),
);

export interface TenantState {
  TenantId: string;
  State: string;
  Locked: boolean;
  LockId?: string;
  LockedBy?: string;
  created: number;
  updated: number;
}

export interface TenantStateUpdateDto {
  state: string;
  locked?: boolean;
  lockId?: string;
  forced?: boolean;
}

// `children` (the page title) sits beside the switch. The locked-by label goes on its own
// line below, so the switch stays in place when the site locks.
export const TenantLockStatus: React.FC = ({ children }) => {
  const { t } = useTranslation();
  const { tenant } = useTenantContext();
  const [displayLocked, setDisplayLocked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const classes = useStyles();
  const { learnInstanceId } = useLearnInstance();
  const tenantId = tenant && tenant?.tenantType === 'LearnInstance' ? tenant.id : learnInstanceId;
  const apiGetEndpoint = apiUrl('tenancy', `tenants/${tenantId}/state`);
  const apiBaseEndpoint = apiUrl('tenancy', `tenants/${tenantId}`);

  const {
    data: tenantState,
    loading,
    error,
    fetch: refetchState,
  } = useRestApi<TenantState>(apiGetEndpoint);

  const {
    doPatch,
    loadingRequests: updatingState,
    failedRequests,
    succeededRequests,
    clearFailedRequests,
    clearSucceededRequests,
  } = useRestApi(apiBaseEndpoint, { manual: true });

  useEffect(() => {
    if (succeededRequests.length) {
      refetchState();
      clearSucceededRequests();
    }
  }, [succeededRequests, refetchState, clearSucceededRequests]);

  useEffect(() => {
    if (failedRequests.length && tenantState) {
      setDisplayLocked(tenantState.Locked);
      clearFailedRequests();
    }
  }, [failedRequests, tenantState, clearFailedRequests]);

  useEffect(() => {
    if (tenantState) {
      setDisplayLocked(tenantState.Locked);
      setIsLoading(false);
    }
  }, [tenantState]);

  const createUpdatePayload = (
    state: string,
    newLockedState: boolean,
    lockId?: string,
  ): TenantStateUpdateDto => {
    const base = { state, locked: newLockedState };
    if (newLockedState) return base;
    if (lockId) return { ...base, lockId };
    return { ...base, forced: true };
  };

  const toggleLockState = useCallback(async () => {
    if (loading || updatingState || isUpdating || !tenantState || !tenant?.id) return;

    setIsUpdating(true);
    const prev = displayLocked;
    const next = !prev;
    setDisplayLocked(next);

    const payload = createUpdatePayload(tenantState.State, next, tenantState.LockId);

    try {
      await doPatch('state', payload);
    } catch {
      setDisplayLocked(prev);
    } finally {
      setIsUpdating(false);
    }
  }, [tenant, tenantState, loading, updatingState, isUpdating, displayLocked, doPatch]);

  const isChecked = !displayLocked;
  const switchLabelText = isChecked ? t('global.unlocked') : t('global.locked');
  const tooltipText = isChecked ? t('tenant.status.lockTooltip') : t('tenant.status.unlockTooltip');
  const lockedBy = tenantState?.Locked ? tenantState.LockedBy : undefined;

  // Loading state
  if (isLoading && !tenantState && !error) {
    return (
      <div className={classes.titleRow}>
        {children}
        <div className={classes.container} data-testid="tenant-lock-status">
          <FormControlLabel
            control={
              <Switch
                id="tenant-lock-switch"
                data-testid="tenant-lock-toggle"
                disabled
                disableRipple
                checked={false}
                classes={{
                  track: classes.track,
                  switchBase: classes.switchBase,
                  checked: classes.checked,
                }}
                icon={
                  <span className={classes.iconWrapper}>
                    <Lock fontSize="small" />
                  </span>
                }
                checkedIcon={
                  <span className={classes.iconWrapper}>
                    <Unlock fontSize="small" />
                  </span>
                }
              />
            }
            label={t('global.loadingIndicator.loading')}
            htmlFor="tenant-lock-switch"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={classes.titleRow}>
        {children}
        <div className={classes.container} data-testid="tenant-lock-status">
          <Tooltip title={tooltipText}>
            <FormControlLabel
              className={classes.switchLabel}
              control={
                <Switch
                  data-testid="tenant-lock-toggle"
                  id="tenant-lock-switch"
                  checked={isChecked}
                  disableRipple
                  onChange={toggleLockState}
                  disabled={isUpdating}
                  inputProps={lockedBy ? { 'aria-describedby': 'tenant-locked-by' } : undefined}
                  classes={{
                    track: classes.track,
                    switchBase: classes.switchBase,
                    checked: classes.checked,
                  }}
                  icon={
                    <span className={classes.iconWrapper}>
                      <Lock fontSize="small" />
                    </span>
                  }
                  checkedIcon={
                    <span className={classes.iconWrapper}>
                      <Unlock fontSize="small" />
                    </span>
                  }
                />
              }
              label={switchLabelText}
              htmlFor="tenant-lock-switch"
            />
          </Tooltip>
        </div>
      </div>
      {lockedBy && (
        <Typography
          id="tenant-locked-by"
          className={classes.lockedBy}
          color="textSecondary"
          data-testid="tenant-locked-by"
        >
          {t('tenant.status.lockedBy', { actor: lockedBy })}
        </Typography>
      )}
    </>
  );
};

export default TenantLockStatus;
